/**
 * A textura dos dados de quem rolou (docs/spec/dice-3d.md, "Textura"): uma imagem do Desk Cloud dele,
 * baixada uma vez por pessoa e guardada já pronta para o atlas dos números (o quadrado do meio, até
 * 512 px, o dobro da célula: o zoom de até 2× continua nítido, e a cor média).
 *
 * O link assinado leva ao bucket, que libera CORS. A cópia é baixada sem cache (a que um `<img>`
 * guardou veio sem esses cabeçalhos) e lida do blob: o canvas nunca fica "sujo", e o WebGL aceita a
 * imagem. Quem vê a rolagem só consegue o link porque o servidor ligou o arquivo à mesa na rolagem.
 * A prévia das Configurações mostra a imagem já baixada (`useDiceTexturePreview`): o mesmo recorte
 * que vai para o dado, sem pedir de novo ao bucket.
 */
import { useEffect, useState } from 'react';
import { fileUrl } from '../files.js';
import { fetchDroppedImage } from '../room/droppedImage.js';
import { DICE_TEXTURE_LIMITS, deskFileRefId } from '../shared.js';
import { useApp } from '../store.js';
import type { AtlasImage } from './dieMesh.js';

/** Imagens guardadas ao mesmo tempo (as mais antigas saem). */
const MAX_CACHED = 24;
/** Quanto tempo uma imagem que não abriu fica sem nova tentativa (ms). */
const RETRY_MS = 60_000;
/** O download que demora mais que isto conta como falha (ms). */
const DOWNLOAD_MS = 20_000;

/** O quadrado do meio de uma imagem `width`×`height`: o recorte que cada face mostra. */
export function centerSquare(width: number, height: number): { x: number; y: number; side: number } {
  const side = Math.min(width, height);
  return { x: (width - side) / 2, y: (height - side) / 2, side };
}

/** A cor média de pixels RGBA, pesada pelo alfa (`#rrggbb`); null se todos são transparentes. */
export function averageColor(rgba: ArrayLike<number>): string | null {
  let r = 0;
  let g = 0;
  let b = 0;
  let weight = 0;
  for (let i = 0; i + 3 < rgba.length; i += 4) {
    const alpha = rgba[i + 3]!;
    r += rgba[i]! * alpha;
    g += rgba[i + 1]! * alpha;
    b += rgba[i + 2]! * alpha;
    weight += alpha;
  }
  if (!weight) return null;
  const hex = (sum: number) => Math.round(sum / weight).toString(16).padStart(2, '0');
  return `#${hex(r)}${hex(g)}${hex(b)}`;
}

/** O valor da promessa, ou `fallback` se ela falhar ou demorar mais que `ms`. Nunca rejeita. */
export function settleWithin<T, F>(promise: Promise<T>, ms: number, fallback: F): Promise<T | F> {
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve(fallback), Math.max(0, ms));
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      () => {
        clearTimeout(timer);
        resolve(fallback);
      },
    );
  });
}

/**
 * Cargas guardadas por chave: a mesma chave pedida duas vezes vira uma carga só, ficam as `max` usadas
 * por último, e uma falha (null ou erro) ganha nova tentativa depois de `retryMs`.
 */
export function loadCache<T>(load: (key: string) => Promise<T | null>, options: { max: number; retryMs: number; now?: () => number }) {
  const now = options.now ?? Date.now;
  const entries = new Map<string, { promise: Promise<T | null>; failedAt: number | null }>();
  return {
    get(key: string): Promise<T | null> {
      const hit = entries.get(key);
      entries.delete(key);
      if (hit && (hit.failedAt === null || now() - hit.failedAt < options.retryMs)) {
        // usada agora: vai para o fim da fila
        entries.set(key, hit);
        return hit.promise;
      }
      const entry: { promise: Promise<T | null>; failedAt: number | null } = { promise: Promise.resolve(null), failedAt: null };
      const failed = () => {
        entry.failedAt = now();
        return null;
      };
      entry.promise = Promise.resolve()
        .then(() => load(key))
        .then((value) => value ?? failed(), failed);
      entries.set(key, entry);
      while (entries.size > options.max) entries.delete(entries.keys().next().value!);
      return entry.promise;
    },
    clear(): void {
      entries.clear();
    },
    get size(): number {
      return entries.size;
    },
  };
}

/** Baixa a imagem de `rpgdesk-file:<id>` e a prepara para o atlas; null se ela não abre. */
async function prepare(ref: string): Promise<AtlasImage | null> {
  const fileId = deskFileRefId(ref);
  if (!fileId) return null;
  const blob = await settleWithin(
    fileUrl(fileId).then((url) => fetchDroppedImage(url, useApp.getState().token)),
    DOWNLOAD_MS,
    null,
  );
  if (!blob) return null;
  const bitmap = await createImageBitmap(blob);
  try {
    const crop = centerSquare(bitmap.width, bitmap.height);
    const side = Math.max(1, Math.min(DICE_TEXTURE_LIMITS.side, Math.round(crop.side)));
    const canvas = document.createElement('canvas');
    canvas.width = side;
    canvas.height = side;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(bitmap, crop.x, crop.y, crop.side, crop.side, 0, 0, side, side);
    // a cor média sai de uma cópia de 8×8
    const tiny = document.createElement('canvas');
    tiny.width = 8;
    tiny.height = 8;
    const small = tiny.getContext('2d', { willReadFrequently: true });
    small?.drawImage(canvas, 0, 0, 8, 8);
    return { image: canvas, side, average: small ? averageColor(small.getImageData(0, 0, 8, 8).data) : null };
  } finally {
    bitmap.close();
  }
}

const cache = loadCache(prepare, { max: MAX_CACHED, retryMs: RETRY_MS });

/** A textura de uma referência, pronta para o atlas (cada imagem é baixada uma vez); null se não abre. */
export function loadDiceTexture(ref: string): Promise<AtlasImage | null> {
  return cache.get(ref);
}

const previews = new WeakMap<AtlasImage, Promise<string | null>>();

/** A imagem pronta como endereço local (`blob:`), para mostrar sem pedir de novo ao bucket. */
function previewUrl(texture: AtlasImage): Promise<string | null> {
  let url = previews.get(texture);
  if (!url) {
    const canvas = texture.image;
    url =
      canvas instanceof HTMLCanvasElement
        ? new Promise((resolve) => canvas.toBlob((blob) => resolve(blob ? URL.createObjectURL(blob) : null), 'image/png'))
        : Promise.resolve(null);
    previews.set(texture, url);
  }
  return url;
}

/**
 * A prévia da textura: a mesma imagem do atlas (o quadrado do meio), como endereço local. null sem
 * textura ou enquanto carrega; 'error' se a imagem não abre.
 */
export function useDiceTexturePreview(ref: string | null): string | null | 'error' {
  const [state, setState] = useState<{ ref: string; src: string | 'error' } | null>(null);
  useEffect(() => {
    if (!ref) return;
    let alive = true;
    void loadDiceTexture(ref)
      .then((texture) => (texture ? previewUrl(texture) : null))
      .then((src) => {
        if (alive) setState({ ref, src: src ?? 'error' });
      });
    return () => {
      alive = false;
    };
  }, [ref]);
  return ref && state?.ref === ref ? state.src : null;
}

// a conta mudou (saiu, ou entrou outra nesta aba): as imagens eram das mesas da anterior
useApp.subscribe((state, previous) => {
  if (state.user?.id !== previous.user?.id) cache.clear();
});
